/**
 * Theme 工具函数
 * 用于处理暗色/亮色主题相关的逻辑
 */

// 检查系统主题偏好
export const getSystemThemePreference = (): 'dark' | 'light' => {
  if (typeof window === 'undefined') return 'light';

  // 检查系统颜色方案偏好
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// 应用主题 CSS 类到 HTML 元素
export const applyThemeClass = (theme: 'dark' | 'light'): void => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

// 获取当前应用的主题（基于html上的dark类）
export const getCurrentTheme = (): 'dark' | 'light' => {
  if (typeof document === 'undefined') return getSystemThemePreference(); // Fallback for SSR or initial load
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
};

// 根据主题模式和系统偏好计算有效主题
export const getEffectiveTheme = (themeMode: 'light' | 'dark' | 'system'): 'light' | 'dark' => {
  if (themeMode === 'system') {
    return getSystemThemePreference();
  }
  return themeMode;
};

// 更新 theme-color meta 标签
export const updateThemeColorMeta = (theme: 'dark' | 'light', activeVersion: string | null): void => {
  if (typeof document === 'undefined') return;

  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (!themeColorMeta) return;

  let color: string; // Renamed for clarity

  if (theme === 'dark') {
    if (activeVersion === 'v0') {
      color = '#2c2c2e'; // V0 dark color
    } else {
      color = '#1C1C1E'; // Default dark color (V3, V1, etc.)
    }
  } else { // theme === 'light'
    if (activeVersion === 'v3') {
      color = '#f2f2f7'; // V3 light color (ios-gray6)
    } else {
      color = '#ffffff'; // Default light color (V0, V1, etc.)
    }
  }

  themeColorMeta.setAttribute('content', color);
};

// 根据系统主题自动设置主题和meta标签
export const initializeSystemTheme = (): void => {
  // 尝试读取主题模式
  let themeMode: 'light' | 'dark' | 'system' = 'system';
  try {
    const storedThemeMode = localStorage.getItem('shared_theme_mode');
    if (storedThemeMode) {
      const parsedMode = JSON.parse(storedThemeMode);
      if (['light', 'dark', 'system'].includes(parsedMode)) {
        themeMode = parsedMode;
      }
    }
  } catch (e) {
    console.error('Error reading theme mode from localStorage:', e);
  }

  // 计算有效主题
  const effectiveTheme = getEffectiveTheme(themeMode);

  // 应用主题
  applyThemeClass(effectiveTheme);

  // 尝试读取初始版本号 (需要与 useVersionRouter 存储方式一致)
  const initialActiveVersion = localStorage.getItem('activeVersion') || 'v0'; // Default to v0 if not found
  updateThemeColorMeta(effectiveTheme, initialActiveVersion);

  // 监听系统主题变化
  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    // 添加变化监听器
    const handleChange = (e: MediaQueryListEvent): void => {
      const newSystemTheme = e.matches ? 'dark' : 'light';

      // 如果当前是system模式，则应用系统主题变化
      try {
        const storedThemeMode = localStorage.getItem('shared_theme_mode');
        if (storedThemeMode) {
          const parsedMode = JSON.parse(storedThemeMode);
          if (parsedMode === 'system') {
            applyThemeClass(newSystemTheme);
            // 当系统主题变化时，也需要知道当前版本来更新 meta
            const currentActiveVersion = localStorage.getItem('activeVersion') || 'v0';
            updateThemeColorMeta(newSystemTheme, currentActiveVersion);
          }
        }
      } catch (e) {
        console.error('Error handling system theme change:', e);
      }
    };

    // 添加事件监听器 (用现代 API, 兼容旧浏览器)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // 旧版浏览器兼容
      mediaQuery.addListener(handleChange);
    }
  }
};